// src/features/stock/components/StockForm.js
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { X, Package, Calendar, Hash, AlertCircle } from 'lucide-react';
import Input from '../../../shared/components/Input';
import Button from '../../../shared/components/Button';
import { UNITS_OF_MEASURE, STOCK_CATEGORIES } from '../constants/stockConstants';

const schema = yup.object({
  name: yup.string().required('Item name is required'),
  sku: yup.string().nullable(),
  category_id: yup.number().required('Category is required'),
  description: yup.string().nullable(),
  quantity: yup.number()
    .positive('Quantity must be positive')
    .required('Quantity is required'),
  unit_id: yup.string().required('Unit of measure is required'),
  reorder_level: yup.number()
  .transform((value, originalValue) =>
    originalValue === "" ? null : value
  )
  .nullable()
    .positive('Reorder level must be positive')
    .nullable(),
 minimium_stock: yup.number()
  .transform((value, originalValue) =>
    originalValue === "" ? null : value
  )
  .nullable()
    .positive('Reorder level must be positive')
    .nullable(),
    // unit_price: yup.number()
  //   .positive('Price must be positive')
  //   .nullable(),
  supplier: yup.string().nullable(),
  location: yup.string().nullable(),
  bin_number: yup.string().nullable(),
  expiry_date: yup.date().nullable(),
  batch_number: yup.string().nullable(),
  notes: yup.string().nullable(),
});

const StockForm = ({ initialData, onSubmit, onClose, isOpen }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialData || {
      quantity: 0,
      unit: 'pcs',
    },
  });

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
  }, [initialData, reset]);

  const handleFormSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data);
      if (!initialData) {
        reset();
      }
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {initialData ? 'Edit Stock Item' : 'Add New Stock Item'}
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Basic Information */}
                <div className="col-span-2">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Basic Information</h4>
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <Input
                    label="Item Name"
                    {...register('name')}
                    error={errors.name?.message}
                    icon={Package}
                    placeholder="Enter item name"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <Input
                    label="SKU (Optional)"
                    {...register('sku')}
                    error={errors.sku?.message}
                    icon={Hash}
                    placeholder="Enter SKU"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    {...register('category_id', { valueAsNumber: true })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value='0'>Select category</option>
                    {STOCK_CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  {errors.category_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.category_id.message}</p>
                  )}
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <Input
                    label="Description"
                    {...register('description')}
                    placeholder="Enter description"
                  />
                </div>

                {/* Stock Details */}
                <div className="col-span-2 mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Stock Details</h4>
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <Input
                    label="Quantity"
                    type="number"
                    step="0.01"
                    {...register('quantity')}
                    error={errors.quantity?.message}
                    placeholder="0"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit of Measure
                  </label>
                  <select
                    {...register('unit', { valueAsNumber: true })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {UNITS_OF_MEASURE.map(unit => (
                      <option key={unit.id} value={unit.id}>{unit.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <Input
                    label="Reorder Level"
                    type="number"
                    {...register('reorder_level')}
                    error={errors.reorder_level?.message}
                    placeholder="Reorder stock level"
                  />
                </div>
                 <div className="col-span-2 sm:col-span-1">
                  <Input
                    label="Minimum Level"
                    type="number"
                    {...register('minimum_stock')}
                    error={errors.minimium_stock?.message}
                    placeholder="Minimum stock level"
                  />
                </div>

                {/* <div className="col-span-2 sm:col-span-1">
                  <Input
                    label="Unit Price"
                    type="number"
                    step="0.01"
                    {...register('unit_price')}
                    error={errors.unit_price?.message}
                    placeholder="0.00"
                  />
                </div> */}

                {/* Storage Information */}
                <div className="col-span-2 mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Storage Information</h4>
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <Input
                    label="Location"
                    {...register('location')}
                    placeholder="e.g., Main Store, Shelf A"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <Input
                    label="Bin Number"
                    {...register('bin_number')}
                    placeholder="e.g., A-01-02"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <Input
                    label="Batch Number"
                    {...register('batch_number')}
                    placeholder="Enter batch number"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <Input
                    label="Expiry Date"
                    type="date"
                    {...register('expiry_date')}
                   //error={errors.expiry_date?.message}
                    icon={Calendar}
                  />
                </div>

                {/* Supplier Information */}
                <div className="col-span-2 mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Supplier Information</h4>
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <Input
                    label="Supplier"
                    {...register('supplier')}
                    placeholder="Enter supplier name"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    {...register('notes')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Additional notes..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={isSubmitting}
                >
                  {initialData ? 'Update Item' : 'Add Item'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockForm;